'use client';

import { Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface PhilosophySectionProps {
  isLoading?: boolean;
}

const principles = [
  {
    number: 1,
    title: 'Determinism grounds judgment.',
    description:
      'Same code, same findings, every run and every reviewer. A steady base to reason from, not a hallucination or a guess that drifts.',
  },
  {
    number: 2,
    title: 'Done is not the same as correct.',
    description: "Agents report success. Mindrealm checks whether it's true.",
  },
  {
    number: 3,
    title: "You decide what's worth fixing.",
    description:
      'Mindrealm flags it; you set the bar. Tune thresholds or mute a check you disagree with. Every call stays on record.',
  },
];

export function PhilosophySection({
  isLoading = false,
}: PhilosophySectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-10 w-[40%]" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                className="mx-auto h-24 w-full max-w-[600px] rounded-xl"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          The convictions behind every review.
        </h2>

        <div className="mx-auto max-w-[700px] space-y-6">
          {principles.map((principle) => (
            <div
              key={principle.number}
              className="rounded-xl p-6"
              style={{
                background: 'transparent',
                border: `1px solid ${theme.box.outline.border[isDark ? 'dark' : 'light']}`,
              }}
            >
              <div className="flex gap-4">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg font-bold"
                  style={{
                    background:
                      theme.box.accentNumber.bg[isDark ? 'dark' : 'light'],
                    color:
                      theme.box.accentNumber.color[isDark ? 'dark' : 'light'],
                  }}
                >
                  {principle.number}
                </span>
                <div>
                  <h3
                    className="mb-1 text-lg font-semibold md:text-xl"
                    style={{ fontFamily: theme.fonts.display }}
                  >
                    {principle.title}
                  </h3>
                  <p
                    className="text-foreground/70 text-base"
                    style={{ fontFamily: theme.fonts.body }}
                  >
                    {principle.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/philosophy"
            className="text-foreground/70 inline-flex items-center gap-2 transition-all hover:underline"
            style={{
              fontFamily: theme.fonts.body,
              textDecorationColor: theme.colors.semantic.info,
            }}
          >
            Read the full philosophy
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
